// Libs
import Compressor from 'compressorjs';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
// Others
import {
  DEFAULT_MAX_HEIGHT_IMAGE,
  DEFAULT_MAX_WIDTH_IMAGE,
  DEFAULT_NUMBER_ONE,
  DEFAULT_NUMBER_ZERO,
  DEFAULT_QUALITY_IMAGE,
  EMPTY_STRING,
  FORMAT_NUMBER,
  PERCENTAGE,
  RANDOM_RANGE,
  REG_EXP,
  TIME_CLOSE_TOAST,
  TIME_OUT_SHOW_TOAST_ERROR,
  TIME_OUT_SHOW_TOAST_SUCCESS,
} from '~/utils/constants/common';
import { IBaseAddress, IBaseUser } from '~/utils/interface/common';
import {
  DateFormatEnum,
  FileDocumentTypeEnum,
  LocaleCurrencyEnum,
  LocaleEnum,
  RoleEnum,
  TimeFormatEnum,
  ToastTypeEnum,
} from '~/utils/enum/common';
import { TIME_OPTIONS } from '../mockData/common';

/**
 * Function to check if an object is empty.
 * @param obj The object to check.
 * @returns True if the object is empty, false otherwise.
 */
export const isEmptyObject = (obj?: object) => {
  if (!obj) return true;

  return Object.keys(obj).length === DEFAULT_NUMBER_ZERO;
};

/**
 * This handle get data time zone
 * @returns data time zone
 */
export const dataTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Generates a unique identifier string.
 * @param randomRange The range within which the random number is generated. Default is 1000.
 * @returns A unique identifier string composed of the current timestamp and a random number.
 */
export const generateUniqueId = (randomRange: number = RANDOM_RANGE): string => {
  return `${Date.now()}-${Math.floor(Math.random() * randomRange)}`;
};

/**
 * Function to format phone numbers in the form (xxx) xxx-xxx.
 * @param value is the value used for formatting.
 * @returns phone number in format.
 */
export const formatPhoneNumber = (value?: string): string => {
  if (!value) return EMPTY_STRING;

  const phoneNumber = value.replace(REG_EXP.NON_DIGIT_REGEX, EMPTY_STRING);
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < FORMAT_NUMBER.numberFour) return phoneNumber;

  if (phoneNumberLength < FORMAT_NUMBER.numberSeven) {
    return `(${phoneNumber.slice(DEFAULT_NUMBER_ZERO, FORMAT_NUMBER.numberThree)}) ${phoneNumber.slice(
      FORMAT_NUMBER.numberThree
    )}`;
  }

  return `(${phoneNumber.slice(DEFAULT_NUMBER_ZERO, FORMAT_NUMBER.numberThree)}) ${phoneNumber.slice(
    FORMAT_NUMBER.numberThree,
    FORMAT_NUMBER.numberSix
  )}-${phoneNumber.slice(FORMAT_NUMBER.numberSix, FORMAT_NUMBER.numberTen)}`;
};

/**
 * Function to remove all non-digit characters from a given string.
 * @param value - The string value that needs to be cleaned of non-digit characters.
 * @returns A string containing only the digits from the value.
 */
export const removeNonDigits = (value?: string) => {
  if (!value) return EMPTY_STRING;

  return value.replace(REG_EXP.NON_DIGIT_REGEX, EMPTY_STRING);
};

/**
 * Function to get the full name of a user by concatenating the first name, middle name, and last name.
 * @param user - The user object containing firstName, middleName, and lastName.
 * @returns A string representing the full name of the user, or an empty string if no name components are provided.
 */
export const getFullName = (user: IBaseUser) => {
  const { firstName, middleName, lastName } = user;

  const nameParts = [firstName?.trim(), middleName?.trim(), lastName?.trim()].filter(Boolean);

  if (nameParts.length === DEFAULT_NUMBER_ZERO) {
    return EMPTY_STRING;
  }

  return nameParts.join(' ');
};

/**
 * Function to get the initials from a person's first and last name.
 * @param params - An object containing the first and last name.
 * @returns A string containing the initials formed by combining the first letter of the first name and the first letter of the last name.
 */
export const getInitials = ({ firstName, lastName }: { firstName?: string; lastName?: string }): string => {
  const firstInitial = firstName?.trim()?.charAt(DEFAULT_NUMBER_ZERO).toUpperCase() || EMPTY_STRING;
  const lastInitial = lastName?.trim()?.charAt(DEFAULT_NUMBER_ZERO).toUpperCase() || EMPTY_STRING;
  return firstInitial + lastInitial;
};

/**
 * Function to format a full address in the US format
 * @param addressObj - The object containing address details.
 * @returns A formatted address string or an empty string if no valid data is provided.
 */
const safeStr = (val: unknown): string => (typeof val === 'string' ? val.trim() : '');

export const getFullAddress = ({ address, city, state, zipCode, country }: IBaseAddress): string => {
  const stateAndZipCode = [safeStr(state), safeStr(zipCode)].filter(Boolean).join(' ');
  const fullAddress = [safeStr(address), safeStr(city), safeStr(stateAndZipCode), safeStr(country)].filter(
    Boolean
  );

  if (fullAddress.length === DEFAULT_NUMBER_ZERO) {
    return EMPTY_STRING;
  }

  return fullAddress.join(', ');
};

/**
 * Function to compress an image file using the Compressor library.
 * @param file - The image file to be compressed.
 * @param quality - The quality of the compressed image file.
 * @returns A promise that resolves to the compressed image file or blob.
 */
const compressedImage = async (file: File, quality: number) => {
  const compressedFile = await new Promise<File | Blob>((resolve, reject) => {
    new Compressor(file, {
      quality,
      maxWidth: DEFAULT_MAX_WIDTH_IMAGE,
      maxHeight: DEFAULT_MAX_HEIGHT_IMAGE,
      success(result) {
        const newFile: File = new File([result], file.name, { type: file.type });
        resolve(newFile);
      },
      error(error) {
        reject(error);
      },
    });
  });

  return compressedFile;
};

/**
 * Creates a FormData object from the provided data, compressing image files if needed.
 * @param {Record<string, any>} data - The data object
 * @param {string} imageKey - The key in the data object that contains the image file to be compressed.
 * @param quality is image quality
 * @returns {Promise<FormData>}
 */
export const createFormData = async (
  data: Record<string, any>,
  imageKey: string,
  quality: number = DEFAULT_QUALITY_IMAGE
): Promise<FormData> => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue;

    if (
      key === imageKey &&
      value.file &&
      typeof value === 'object' &&
      value.file.size > DEFAULT_NUMBER_ZERO
    ) {
      if (value.file) {
        const compressedFile = await compressedImage(value.file, quality);

        formData.append(key, compressedFile);
      }
    } else if (key === imageKey && Array.isArray(value)) {
      for (const file of value) {
        if (file.file && file.file.type.includes('image') && file.file.size > DEFAULT_NUMBER_ZERO) {
          const compressedFile = await compressedImage(file.file, quality);

          formData.append(key, compressedFile);
        } else {
          formData.append(key, file.file);
        }
      }
    } else if (Array.isArray(value) || typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value as string);
    }
  }

  return formData;
};

/**
 * Remove empty objects from an object.
 * @param obj - The object to remove empty objects from.
 * @returns The object with empty objects removed.
 */
export const removeEmptyObjects = (obj: Record<string, any> | null | undefined): Record<string, any> => {
  // Handle null/undefined or non-object inputs
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return {};
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Helper to check if a value is considered empty
    const isValueEmpty = (val: any): boolean => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string' && val.trim() === EMPTY_STRING) return true;
      if (typeof val === 'object' && !Array.isArray(val)) {
        return Object.keys(removeEmptyObjects(val)).length === DEFAULT_NUMBER_ZERO;
      }
      return false;
    };

    // Check if current value should be removed
    if (isValueEmpty(value)) {
      return acc;
    }

    // Process arrays
    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            // Preserve objects with 'file' key (special case)
            if ('file' in item) return item;
            return removeEmptyObjects(item);
          }
          return item;
        })
        .filter((item) => !isValueEmpty(item)); // Remove empty items

      if (cleanedArray.length > DEFAULT_NUMBER_ZERO) {
        acc[key] = cleanedArray;
      }
      return acc;
    }

    // Process plain objects (excluding special objects like Date)
    if (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Object]') {
      if ('file' in value) {
        acc[key] = value;
        return acc;
      }

      const cleanedValue = removeEmptyObjects(value);
      if (Object.keys(cleanedValue).length > DEFAULT_NUMBER_ZERO) {
        acc[key] = cleanedValue;
      }
      return acc;
    }

    // Preserve other values (numbers, booleans, non-empty strings, etc.)
    acc[key] = value;
    return acc;
  }, {} as Record<string, any>);
};

/**
 * Function to check if the given mineType is an image type.
 * @param {string} mineType - The MIME type of the file.
 * @returns {boolean} True if the mineType is an image type, false otherwise.
 */
export const checkTypeImage = (mineType: string): boolean => {
  return mineType.includes(FileDocumentTypeEnum.IMAGE);
};

/**
 * Converts a string to title case.
 * @param str - The string to be converted.
 * @returns The converted string.
 */
export const formatTitleCase = (str?: string) => {
  if (!str) return EMPTY_STRING;
  return str
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Converts a string to title case.
 * @param str - The string to be converted.
 * @returns The converted string.
 */
export const convertToTitleCase = (str: string): string => {
  const newStr = str
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return newStr;
};

/**
 * Handle show toast message.
 * @param type is option of reducer.
 * @param message is option of reducer.
 * @returns modal toast by status
 */
export const customToast = (type: ToastTypeEnum, message: string) => {
  switch (type) {
    case ToastTypeEnum.ERROR:
      setTimeout(() => {
        toast.error(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_ERROR);
      break;
    case ToastTypeEnum.SUCCESS:
      setTimeout(() => {
        toast.success(message, {
          autoClose: TIME_CLOSE_TOAST,
        });
      }, TIME_OUT_SHOW_TOAST_SUCCESS);
      break;
    case ToastTypeEnum.WARNING:
      toast.warning(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
    default:
      toast(message, {
        autoClose: TIME_CLOSE_TOAST,
      });
      break;
  }
};

/**
 * Date format.
 * @param date is the time series that needs formatting.
 * @param format the date type you want to format.
 * @returns string after formatting.
 */
export const formatDate = (date: string | Date | undefined, format?: DateFormatEnum): string => {
  if (!date) return EMPTY_STRING;
  return moment(date).format(format || DateFormatEnum.DD_MMM_YYYY);
};

/**
 * Date format.
 * @param date is the time series that needs formatting.
 * @param format the date type you want to format.
 * @param timezone the timezone
 * @returns string after formatting.
 */
export const formatDateByTimeZone = (
  date: string | Date | undefined,
  format?: DateFormatEnum,
  timezone?: string
): string => {
  if (!date) return EMPTY_STRING;
  const timezoneValue = timezone || dataTimeZone();
  return moment.tz(date, timezoneValue).format(format || DateFormatEnum.DD_MMM_YYYY);
};

/**
 * Validate value with regex.
 * @param value is the validate value.
 * @param regex is the regex used to check.
 * @returns true if regex is satisfied and vice versa.
 */
export const validateWithRegex = (value: string, regex: RegExp): boolean => {
  return regex.test(value);
};

/**
 * Convert code to role.
 * @param role is the role code.
 * @returns the role name.
 */
export const convertCodeToRole = (role: string): string => {
  if (!role) return EMPTY_STRING;

  switch (role) {
    case RoleEnum.ADMIN:
      return 'common_admin_title';
    case RoleEnum.STAFF:
      return 'common_staff_title';
    case RoleEnum.TECHNICIAN:
      return 'common_technician_title';
    default:
      return EMPTY_STRING;
  }
};

/**
 * Converts a snake_case or UPPER_SNAKE_CASE string to camelCase.
 * @param text The input string in snake_case or UPPER_SNAKE_CASE format.
 * @returns The converted string in camelCase format.
 */
export const toCamelCase = (text?: string): string => {
  if (!text) return EMPTY_STRING;

  return text
    .toLowerCase()
    .split('_')
    .map((word, index) =>
      index === DEFAULT_NUMBER_ZERO
        ? word
        : word.charAt(DEFAULT_NUMBER_ZERO).toUpperCase() + word.slice(DEFAULT_NUMBER_ONE)
    )
    .join('');
};

/**
 * Formats a given time string into a specified format.
 * @param time The input time string (e.g., "14:30", "08:45 AM", "23:15:30").
 * @param format The desired output format (default: 'hh:mm A').
 * @returns The formatted time string or an empty string if invalid.
 */
export const formatTime = (time?: string, format: TimeFormatEnum = TimeFormatEnum.HH_MM_A_UPPER): string => {
  if (!time) return EMPTY_STRING;

  const parsedTime = moment(time).format(format);

  return parsedTime;
};

/**
 * Formats a given time string into a specified format.
 * @param time The input time string (e.g., "14:30", "08:45 AM", "23:15:30").
 * @param format The desired output format (default: 'hh:mm A').
 * @returns The formatted time string or an empty string if invalid.
 */
export const formatTimeUTC = (
  time?: string,
  format: TimeFormatEnum = TimeFormatEnum.HH_MM_A_UPPER
): string => {
  if (!time) return EMPTY_STRING;

  const parsedTime = moment(time).format(format);

  return parsedTime;
};

/**
 * Converts a string to a color value.
 * @param string The input string to convert.
 * @returns The color value represented by the input string.
 */
export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

/**
 * Compares two objects and returns a partial object containing the updated values.
 * @param originalData - The original data object.
 * @param editedData - The edited data object.
 * @returns An object containing keys from editedData with values that differ from originalData.
 */
export const compareDataUpdate = (
  originalData: { [key: string]: any },
  editedData: { [key: string]: any }
): { [key: string]: any } => {
  const result: { [key: string]: any } = {};

  Object.entries(originalData).forEach(([key, value]) => {
    if (Array.isArray(value) && Array.isArray(editedData[key])) {
      const changedArray = editedData[key].reduce((acc, editedItem, index) => {
        const originalItem = value[index];

        if (!originalItem || JSON.stringify(originalItem) !== JSON.stringify(editedItem)) {
          acc.push(editedItem);
        }
        return acc;
      }, [] as any[]);

      if (changedArray.length > DEFAULT_NUMBER_ZERO) {
        result[key] = changedArray;
      }
    } else if (JSON.stringify(value) !== JSON.stringify(editedData[key])) {
      result[key] = editedData[key];
    }
  });

  Object.keys(editedData).forEach((key) => {
    if (!(key in originalData)) {
      result[key] = editedData[key];
    }
  });
  return result;
};

/**
 * Generates an avatar name abbreviation from a full name.
 * @param name The full name as a string.
 * @returns A string containing the first letter of the first and last name.
 */
export const getAvatarName = (name: string) => {
  if (!name) return EMPTY_STRING;

  return `${name.split(' ')[DEFAULT_NUMBER_ZERO][DEFAULT_NUMBER_ZERO]}${
    name.split(' ')[DEFAULT_NUMBER_ONE][DEFAULT_NUMBER_ZERO]
  }`;
};

/**
 * Get current date UTC.
 * @param format is date format.
 * @returns current date in UTC with input format.
 */
export const getCurrentDateUTC = (
  date: Date | string = new Date(),
  format: DateFormatEnum = DateFormatEnum.YYYY_MM_DD
) => {
  return moment(date).utc().format(format);
};

/**
 * Get current time UTC.
 * @returns current next time option format.
 */
export const getNextTimeOption = (): string => {
  const now = moment();
  const currentTime = now.format(TimeFormatEnum.HH_MM);

  const nextTime = TIME_OPTIONS.find((time) => time >= currentTime);

  return nextTime ?? TIME_OPTIONS[DEFAULT_NUMBER_ZERO];
};

/**
 * Get current time UTC.
 * @returns current next time option format.
 */
export const getEndTimeOption = (startTime: string, duration: number = DEFAULT_NUMBER_ONE): string => {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const targetTime = (startHour + duration) * 60 + startMinute;

  const endTime = TIME_OPTIONS.find((time) => {
    const [hour, minute] = time.split(':').map(Number);
    return hour * 60 + minute >= targetTime;
  });

  return endTime ?? TIME_OPTIONS[DEFAULT_NUMBER_ZERO];
};

/**
 * Format the value to local decimal form.
 * @param number is the value to be formatted.
 * @param fractionDigits is fraction digits to value.
 * @returns
 */
export const formatDecimal = (number: number, fractionDigits?: number): string => {
  return new Intl.NumberFormat(LocaleEnum.USD, {
    style: 'decimal',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(number);
};

/**
 * Formats the input string into a decimal format.
 * @param value The string value to be formatted.
 * @returns The formatted decimal string.
 */
export const formatInputDecimal = (value: string, maxNumber?: number) => {
  if (value.startsWith('.')) {
    return EMPTY_STRING;
  }

  let inputValue = value.replace(/[^\d.]/g, EMPTY_STRING);

  const parts = inputValue.split('.');
  if (parts[0].length > 1) {
    parts[0] = parts[0].replace(/^0+/, EMPTY_STRING);
    if (parts[0] === EMPTY_STRING) {
      parts[0] = '0';
    }
  }

  inputValue = parts.join('.');

  const decimalCount = (inputValue.match(/\./g) || []).length;
  if (decimalCount > DEFAULT_NUMBER_ONE) {
    return inputValue.slice(0, -1);
  }

  if (parts.length > 1 && parts[1].length > 2) {
    inputValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
  }

  if (maxNumber && inputValue !== EMPTY_STRING) {
    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue) && numericValue > maxNumber) {
      return maxNumber.toString();
    }
  }

  return inputValue;
};

/**
 * Converts a UTC timestamp string to a formatted local time string.
 * @param utcTimestamp - The UTC timestamp string in ISO format (e.g., "2024-06-15T09:07:18.140Z").
 * @param localFormat - (Optional) The desired format for the local time. Defaults to the browser's locale time format. If provided, it should follow the Moment.js format syntax (https://momentjs.com/).
 * @returns The formatted local time string in the specified format (or the browser's locale format if none is provided).
 */
export const convertUTCTimeToLocalTime = (utcTimestamp: string, localFormat?: TimeFormatEnum): string => {
  const localTime = moment.utc(utcTimestamp).local();
  const formattedLocalTime = localTime.format(localFormat);

  return formattedLocalTime;
};

/**
 * Get file extension
 * @param {string} url is link file string
 */
export const getFileExtension = (url?: string): string => {
  if (!url || !url.includes('.')) return EMPTY_STRING;

  return url.split('.').pop()?.toLowerCase() || EMPTY_STRING;
};

/**
 * Converts a date string to a relative time string (e.g., "30 minutes ago").
 * @param {string | Date} date - The date string to convert.
 * @returns {string} - The relative time string.
 */
export const convertToRelativeTime = (date: string | Date): string => {
  return moment(date).fromNow();
};

/**
 * Function to convert minute to hh:mm.
 * @param minutes is minutes.
 * @returns hh:mm ex: 02:15.
 */
export const convertMinuteToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainMinutes = minutes % 60;

  return `${hours.toString().padStart(2, '0')}:${remainMinutes.toString().padStart(2, '0')}`;
};

/**
 * Format the value to local decimal form.
 * @param price is the value money to be formatted.
 * @param symbol is the unit of money.
 * @returns American currency format
 */
export const formatCurrency = (price?: string | number, locale?: string): string => {
  const localType = locale ? locale : LocaleCurrencyEnum.LOCALE_EN;

  let option = {};

  switch (localType) {
    case LocaleCurrencyEnum.LOCALE_VI: {
      option = {
        style: LocaleCurrencyEnum.STYLE,
        currency: LocaleCurrencyEnum.CURRENCY_VND,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      };
      break;
    }
    default: {
      option = {
        style: LocaleCurrencyEnum.STYLE,
        currency: LocaleCurrencyEnum.CURRENCY_USD,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      };
    }
  }

  const formattedPrice = Number(price).toLocaleString(localType, option);

  return `${formattedPrice}`;
};

export const generateGuid = (): string => {
  const generateSegment = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  const guid =
    generateSegment() +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    '-' +
    generateSegment() +
    generateSegment() +
    generateSegment();

  return guid;
};

/**
 * Export a file from a string content.
 *
 * @param filename - The name of the file to be downloaded (e.g., 'data.csv')
 * @param content - The content of the file
 * @param mimeType - The MIME type of the file (default: 'text/csv')
 */
export const exportFile = (filename: string, content: string, mimeType: string = 'text/csv'): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
};

/**
 * Function to split a full name into first name and last name.
 * @param fullName The full name string.
 * @returns An object with firstName and lastName. If fullName is empty, both are empty strings.
 */
export const splitFullName = (fullName?: string): { firstName: string; lastName: string } => {
  if (!fullName) {
    return {
      firstName: EMPTY_STRING,
      lastName: EMPTY_STRING,
    };
  }

  const parts = fullName.trim().split(/\s+/);

  if (parts.length === DEFAULT_NUMBER_ZERO) {
    return {
      firstName: EMPTY_STRING,
      lastName: EMPTY_STRING,
    };
  }

  const [firstName, ...rest] = parts;

  return {
    firstName,
    lastName: rest.join(' ') || EMPTY_STRING,
  };
};

/**
 * Extracts the name and email from a string.
 * @param {string} input The input string containing the name and email.
 * @returns {Object} An object containing the name and email.
 */
export const extractNameAndEmail = (input: string): { name: string; email: string } => {
  const match = input.match(/^(.+?)\s*(<.+?>)$/);
  if (match) {
    return {
      name: match[1].trim(),
      email: match[2].trim(),
    };
  }
  return {
    name: input,
    email: EMPTY_STRING,
  };
};

/**
 * Display a percentage value.
 * @param {number} value Value is a number.
 * @returns {string} value will become like <number>%.
 */
export const displayPercentageVal = (value?: number): string => {
  if (!value || isNaN(value)) return '';

  const result = value + PERCENTAGE;
  return result;
};
